// CC06 · Procedencia de la información de una oferta.
// Muestra, para los campos clave, si el dato es confirmado / derivado / inferido
// / desconocido — para que nadie trate un valor estimado como un hecho.
import React, { useState, useEffect } from "react";
import axios from "axios";
import { ShieldCheck, ChevronDown } from "lucide-react";
import { API } from "@/context/AuthContext";

const COLORS = {
  emerald: "bg-emerald-100 text-emerald-800 border-emerald-200",
  sky: "bg-sky-100 text-sky-800 border-sky-200",
  amber: "bg-amber-100 text-amber-800 border-amber-200",
  slate: "bg-slate-100 text-slate-600 border-slate-200",
};
const CLASS_COLOR = { confirmado: "emerald", derivado: "sky", inferido: "amber", desconocido: "slate" };

function Chip({ clase, label, n }) {
  const c = COLORS[CLASS_COLOR[clase]] || COLORS.slate;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-semibold ${c}`} data-testid={`provenance-count-${clase}`}>
      {label}: {n}
    </span>
  );
}

export default function ProvenancePanel({ oid }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [err, setErr] = useState(false);

  useEffect(() => {
    if (!oid) return;
    axios.get(`${API}/ofertas-comercial/${oid}/provenance`)
      .then(r => setData(r.data)).catch(() => setErr(true));
  }, [oid]);

  if (!oid || err || !data) return null;
  const { summary, fields, classes } = data;

  return (
    <div className="border-b border-gray-100 px-4 py-2 bg-slate-50/70" data-testid="oferta-provenance-panel">
      <button type="button" onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-2 text-[11px] font-semibold text-slate-700"
        data-testid="oferta-provenance-toggle">
        <ShieldCheck size={12} className="text-emerald-600"/>
        Procedencia de los datos
        <span className="flex flex-wrap gap-1 ml-1">
          {["confirmado", "derivado", "inferido", "desconocido"].map(k =>
            (summary[k] ? <Chip key={k} clase={k} label={classes[k].label} n={summary[k]}/> : null))}
        </span>
        <ChevronDown size={11} className={`ml-auto transition-transform ${open ? "rotate-180" : ""}`}/>
      </button>

      {open && (
        <div className="mt-2 space-y-1" data-testid="oferta-provenance-detail">
          <p className="text-[10px] text-slate-500 mb-1">
            Un dato <b>inferido</b> o <b>desconocido</b> nunca se cuenta como un hecho confirmado en los KPIs oficiales.
          </p>
          {fields.map(f => {
            const c = COLORS[CLASS_COLOR[f.clase]] || COLORS.slate;
            return (
              <div key={f.key} className="flex items-start gap-2 text-[11px] py-0.5 border-t border-slate-100 first:border-0"
                   data-testid={`provenance-field-${f.key}`}>
                <span className={`mt-0.5 px-1.5 py-0.5 rounded border text-[9px] font-bold uppercase shrink-0 ${c}`}>
                  {classes[f.clase].label}
                </span>
                <div className="min-w-0">
                  <span className="font-semibold text-slate-700">{f.label}:</span>{" "}
                  <span className="text-slate-600">{f.valor ?? "—"}</span>
                  {f.fuente && <span className="text-slate-400"> · fuente: {f.fuente}</span>}
                  {f.nota && <div className="text-[10px] text-slate-500 italic">{f.nota}</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
